import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {AuthService} from '../../../auth.service';
import {TranslateService} from '../../../../shared/translate.service';
import {FormControl} from '@angular/forms';
import {BehaviorSubject, of, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, startWith} from 'rxjs/operators';
import {UserCollectionDtoInterface} from '../dto/user-collection-dto';
import {UserCollectionsService} from '../user-collections.service';
import {PageResult} from '../../../../shared/page-result';
import {MessageService} from '../../../../messages/message.service';
import {FavouriteButtonService} from "./favourite-button.service";
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {FavouriteUserCollectionModalComponent} from '../favourite-user-collection-modal/favourite-user-collection-modal.component';

@Component({
  selector: 'app-favourite-button',
  templateUrl: './favourite-button.component.html',
  styleUrls: ['./favourite-button.component.scss']
})
export class FavouriteButtonComponent implements OnInit, OnDestroy {

  @Input() targetId: string;
  @Input() targetType: string;
  @Input() ariaLabel: string;
  @Input() checkAsyncIsFavourite = false;

  readonly ariaLabelNotLoggedIn: string = this.translateService.translate('Favourite.notLoggedIn');
  enabled = false;
  favourite = false;
  count = 0;

  showSelect = false;
  selectSubscription: Subscription;

  collectionsSelect = new FormControl();
  filteredCollections = new BehaviorSubject<UserCollectionDtoInterface[]>([]);

  subscriptionFavourites: Subscription;
  subscriptionLogged: Subscription;

  constructor(private authService: AuthService,
              private translateService: TranslateService,
              private userCollections: UserCollectionsService,
              private messageService: MessageService,
              private favouriteButtonService: FavouriteButtonService,
              protected modalService: NgbModal) {
    this.selectSubscription = this.favouriteButtonService.getSelectStatus().subscribe(showSelect => { this.showSelect = showSelect; });
  }

  ngOnInit() {
    this.subscriptionLogged = this.authService.isLogged.subscribe(logged => {
      this.enabled = logged;
      if (logged){
        this.showAll();
        this.collectionsSelect.valueChanges
          .pipe(
            debounceTime(500),
            distinctUntilChanged(),
            startWith<string | UserCollectionDtoInterface>(''),
            map(value => typeof value === 'string' ? value : value.name),
            map(name => name && name.trim().length > 0 ? this._filter(name) : this.showAll())
          ).subscribe(result => of());
        this.checkIsFavourite();
      }
    });
  }

  private checkIsFavourite() {
    try{
      if (this.checkAsyncIsFavourite) {
        this.checkIsFavouriteAsync();
      } else {
        this.checkIsFavouriteFromCurrentSearchPage();
      }
    }catch (e) {
      console.log(e);
    }
  }

  private checkIsFavouriteAsync() {
    this.authService.getToken().then(token => {
      this.userCollections.getFavourites([this.targetId], token).subscribe(value => {
        this.favourite = value.length > 0;
      });
    });
  }

  private checkIsFavouriteFromCurrentSearchPage() {
    this.subscriptionFavourites = this.userCollections.favourites.subscribe(value => {
      this.favourite = value.indexOf(this.targetId) >= 0;
    });
  }

  ngOnDestroy(): void {
    if (this.subscriptionFavourites){
      this.subscriptionFavourites.unsubscribe();
    }
    if (this.subscriptionLogged){
      this.subscriptionLogged.unsubscribe();
    }
  }

  displayFn(user?: UserCollectionDtoInterface): string | undefined {
    return user ? user.name : undefined;
  }

  saveFavourite() {
    this.toggleSelect();
  }

  removeFromFavourites() {
    this.toggleSelect();
  }

  toggleSelect() {
    const modalInstance = this.modalService.open(FavouriteUserCollectionModalComponent);
    modalInstance.componentInstance.titleName = this.ariaLabel;
    modalInstance.componentInstance.titleId = this.targetId;
    modalInstance.componentInstance.titleType = this.targetType;
    modalInstance.result.then(() => {
      this.checkIsFavouriteAsync();
    });
  }

  showDisabled() {
    this.messageService.errorMessage('','Favourite.notLoggedIn');
  }

  private _filter(name: string) {
    const filterValue = name ? name.toLowerCase() : '';
    this.authService.getToken().then(token => {
      this.userCollections.findList(0, 5, 'id', 'asc', token, {name: filterValue})
        .subscribe((page: PageResult<UserCollectionDtoInterface>) => {
          this.count = page.totalElements;
          this.filteredCollections.next(page.content);
        });
    });
  }

  private showAll() {
    this.count = this.userCollections.count;
    this.filteredCollections.next(this.userCollections.initData.value);
  }
}
